import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Globe, Plus, Pencil, DollarSign, ShoppingBag, CheckCircle2,
  XCircle, Loader2, ExternalLink, BarChart3, Clock, Send,
  Sparkles, Wifi, WifiOff, FileText, Tag, MapPin, Users, Link2, Percent, Info, Search
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Technologia", "Biznes", "Zdrowie", "Finanse", "Lifestyle",
  "Motoryzacja", "Sport", "Edukacja", "Marketing", "Kultura"
];
const REGIONS = ["Cala Polska", "Warszawa", "Krakow", "Wroclaw", "Poznan", "Europa"];

const statusBadge = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  accepted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  rejected: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
};

export default function PublisherDashboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sites");
  const [addDialog, setAddDialog] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteTab, setSiteTab] = useState("overview");
  const [analyzing, setAnalyzing] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [testingWp, setTestingWp] = useState(false);
  const [pricingItems, setPricingItems] = useState([]);
  const [editingPricing, setEditingPricing] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);
  const [wpCategories, setWpCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState(""); // Wyszukiwanie
  const [siteForm, setSiteForm] = useState({
    name: "", url: "", category: "Technologia", region: "Cala Polska",
    monthly_visitors: 1000, publication_price: 100, writing_price: 200,
    link_price: 50, turnaround_days: 3, description: "",
    site_type: "wordpress", wp_api_user: "", wp_api_pass: "",
    ftp_host: "", ftp_user: "", ftp_pass: "",
    manual_approval: false,
  });

  // Filtrowane strony
  const filteredSites = sites.filter(site => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (site.name || "").toLowerCase().includes(query) ||
      (site.url || "").toLowerCase().includes(query)
    );
  });

  // Prowizja portalu
  const [commissionPercent, setCommissionPercent] = useState(5.0);

  const fetchData = useCallback(async () => {
    try {
      const [sitesRes, ordersRes, statsRes, commissionRes] = await Promise.all([
        axios.get(`${API}/publisher/my-sites`),
        axios.get(`${API}/publisher/my-orders${orderFilter !== "all" ? `?status=${orderFilter}` : ""}`),
        axios.get(`${API}/publisher/stats`),
        axios.get(`${API}/public/commission`).catch(() => ({ data: { commission_percent: 5.0 } })),
      ]);
      setSites(sitesRes.data);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
      setCommissionPercent(commissionRes.data.commission_percent || 5.0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [orderFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddSite = async () => {
    // Walidacja - sprawdź czy domena już istnieje
    const urlNormalized = siteForm.url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
    const existingDomain = sites.find(site => {
      const siteUrl = (site.url || "").toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
      return siteUrl === urlNormalized;
    });
    
    if (existingDomain) {
      toast.error(`Domena "${urlNormalized}" już istnieje w Twoich stronach!`);
      return;
    }
    
    try {
      await axios.post(`${API}/publisher/add-site`, siteForm);
      toast.success(t("pub.siteAdded"));
      fetchData(); setAddDialog(false);
      setSiteForm({ name: "", url: "", category: "Technologia", region: "Cala Polska",
        monthly_visitors: 1000, publication_price: 100, writing_price: 200,
        link_price: 50, turnaround_days: 3, description: "",
        site_type: "wordpress", wp_api_user: "", wp_api_pass: "",
        ftp_host: "", ftp_user: "", ftp_pass: "", manual_approval: false });
    } catch (err) { 
      if (err.response?.data?.detail?.includes('duplikat') || err.response?.data?.detail?.includes('exists') || err.response?.data?.detail?.includes('już istnieje')) {
        toast.error("Ta domena już istnieje w systemie!");
      } else {
        toast.error(err.response?.data?.detail || t("common.error")); 
      }
    }
  };

  const handleOrderAction = async (orderId, action) => {
    setProcessing(orderId);
    try {
      await axios.post(`${API}/publisher/orders/${orderId}/${action}`);
      toast.success(t("common.success"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
    finally { setProcessing(null); }
  };

  const [generating, setGenerating] = useState(null);
  
  const handleGenerateArticle = async (orderId) => {
    setGenerating(orderId);
    try {
      const res = await axios.post(`${API}/admin/orders/${orderId}/generate-article`);
      toast.success(`Artykul wygenerowany: "${res.data.title}" (${res.data.content_length} slow)`);
      fetchData();
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Blad generowania artykulu"); 
    }
    finally { setGenerating(null); }
  };

  // --- Site management functions (same as admin) ---
  const handleAnalyzeSite = async (site) => {
    setAnalyzing(true);
    try {
      await axios.post(`${API}/sites/wp/${site.id}/analyze`, { ai_model: "gemini" });
      toast.success("Analiza zakonczona");
      fetchData();
      const updated = await axios.get(`${API}/sites/${site.id}`);
      setSelectedSite(updated.data);
    } catch (err) { toast.error(t("common.error")); }
    finally { setAnalyzing(false); }
  };

  const handleGeneratePricing = async (site) => {
    setPricingLoading(true);
    try {
      await axios.post(`${API}/sites/wp/${site.id}/pricing`, { ai_model: "gemini" });
      toast.success("Cennik wygenerowany");
      fetchData();
      const updated = await axios.get(`${API}/sites/${site.id}`);
      setSelectedSite(updated.data);
    } catch (err) { toast.error(t("common.error")); }
    finally { setPricingLoading(false); }
  };

  const handleTestConnection = async (site) => {
    setTestingWp(true);
    try {
      const res = await axios.post(`${API}/sites/wp/test`, { url: site.url });
      toast[res.data.success ? "success" : "error"](res.data.success ? "Polaczenie OK" : (res.data.error || "Blad"));
    } catch (err) { toast.error(t("common.error")); }
    finally { setTestingWp(false); }
  };

  const handleTestPublish = async (site) => {
    try {
      const res = await axios.post(`${API}/sites/wp/${site.id}/test-publish`);
      toast[res.data.success ? "success" : "error"](res.data.success ? "Test publikacji OK!" : (res.data.error || "Blad"));
      fetchData();
      const updated = await axios.get(`${API}/sites/${site.id}`);
      setSelectedSite(updated.data);
    } catch (err) { toast.error("Blad testu publikacji"); }
  };

  const handleSavePricing = async (site, pricingData) => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/sites/wp/${site.id}/pricing`, { pricing: pricingData });
      toast.success("Cennik zapisany");
      fetchData();
      const updated = await axios.get(`${API}/sites/${site.id}`);
      setSelectedSite(updated.data);
    } catch (err) { toast.error(t("common.error")); }
    finally { setSavingPricing(false); }
  };

  const openSiteDetail = (site) => {
    setSelectedSite(site);
    setSiteTab("overview");
    if (site.pricing?.pricing) {
      setPricingItems(site.pricing.pricing.map(item => ({ ...item, enabled: item.enabled !== false })));
    } else {
      setPricingItems([]);
    }
  };

  return (
    <div data-testid="publisher-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("pub.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1 font-body">{t("pub.subtitle")}</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: t("pub.mySites"), value: stats.sites, icon: Globe, color: "text-blue-500" },
            { label: t("pub.totalOrders"), value: stats.total_orders, icon: ShoppingBag, color: "text-slate-500" },
            { label: t("pub.pendingOrders"), value: stats.pending, icon: Clock, color: "text-amber-500" },
            { label: t("pub.completedOrders"), value: stats.completed, icon: CheckCircle2, color: "text-emerald-500" },
            { label: t("pub.revenue"), value: `${stats.revenue} PLN`, icon: DollarSign, color: "text-primary" },
          ].map((s, i) => (
            <Card key={i} className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-3 flex items-center gap-2">
                <s.icon className={`w-4 h-4 ${s.color}`} strokeWidth={1.5} />
                <div>
                  <p className="text-lg font-bold font-heading text-foreground">{s.value}</p>
                  <p className="text-[9px] text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="pub-tabs">
          <TabsTrigger value="sites" className="gap-1.5">
            <Globe className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("pub.sitesTab")}
            <Badge variant="secondary" className="text-[10px] rounded-md ml-1">{sites.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="orders" className="gap-1.5">
            <ShoppingBag className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("pub.ordersTab")}
            {orders.filter(o => o.status === "pending").length > 0 && (
              <Badge className="bg-amber-100 text-amber-700 text-[10px] border-0 rounded-md ml-1">
                {orders.filter(o => o.status === "pending").length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* SITES TAB */}
        <TabsContent value="sites" className="mt-4 space-y-4">
          <div className="flex justify-between items-center gap-3">
            {/* Wyszukiwarka */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Szukaj strony..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Dialog open={addDialog} onOpenChange={setAddDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-pub-site" className="rounded-lg gap-2">
                  <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("pub.addSite")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">{t("pub.addSite")}</DialogTitle></DialogHeader>
                <div className="grid gap-3 py-4">
                  {/* Podstawowe */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("pub.siteName")}</Label>
                      <Input data-testid="pub-site-name" value={siteForm.name} onChange={(e) => setSiteForm({...siteForm, name: e.target.value})} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">URL</Label>
                      <Input value={siteForm.url} onChange={(e) => setSiteForm({...siteForm, url: e.target.value})} placeholder="https://..." />
                    </div>
                  </div>

                  {/* Platforma */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("pub.platform")}</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: "wordpress", label: "WordPress" },
                        { id: "html", label: "HTML / Statyczna" },
                        { id: "other", label: "Inna" },
                      ].map((p) => (
                        <button key={p.id} type="button"
                          onClick={() => setSiteForm({...siteForm, site_type: p.id})}
                          className={`p-2.5 rounded-lg border text-xs font-medium text-center transition-all ${
                            siteForm.site_type === p.id ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/30"
                          }`}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* WordPress API credentials */}
                  {siteForm.site_type === "wordpress" && (
                    <div className="p-3 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 space-y-2">
                      <p className="text-[10px] font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider">WordPress REST API</p>
                      <p className="text-[9px] text-muted-foreground">Podaj dane do WordPress REST API aby system mogl automatycznie publikowac artykuly.</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[10px]">{t("pub.wpUser")}</Label>
                          <Input value={siteForm.wp_api_user} onChange={(e) => setSiteForm({...siteForm, wp_api_user: e.target.value})} placeholder="admin" className="h-8 text-xs" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px]">{t("pub.wpPass")}</Label>
                          <Input type="password" value={siteForm.wp_api_pass} onChange={(e) => setSiteForm({...siteForm, wp_api_pass: e.target.value})} placeholder="Application Password" className="h-8 text-xs" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* HTML/FTP credentials */}
                  {siteForm.site_type === "html" && (
                    <div className="p-3 rounded-lg bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 space-y-2">
                      <p className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">FTP / SFTP</p>
                      <p className="text-[9px] text-muted-foreground">Podaj dane FTP aby system mogl uploadowac pliki na serwer.</p>
                      <Input value={siteForm.ftp_host} onChange={(e) => setSiteForm({...siteForm, ftp_host: e.target.value})} placeholder="ftp.example.com" className="h-8 text-xs" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={siteForm.ftp_user} onChange={(e) => setSiteForm({...siteForm, ftp_user: e.target.value})} placeholder="User FTP" className="h-8 text-xs" />
                        <Input type="password" value={siteForm.ftp_pass} onChange={(e) => setSiteForm({...siteForm, ftp_pass: e.target.value})} placeholder="Haslo FTP" className="h-8 text-xs" />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("pub.category")}</Label>
                      <Select value={siteForm.category} onValueChange={(v) => setSiteForm({...siteForm, category: v})}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("pub.region")}</Label>
                      <Select value={siteForm.region} onValueChange={(v) => setSiteForm({...siteForm, region: v})}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{REGIONS.map(r => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator />

                  {/* Cennik */}
                  <p className="text-xs font-semibold text-foreground">{t("pub.pricing")}</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1.5">
                      <Label className="text-[10px]">{t("pub.pubPrice")} (PLN)</Label>
                      <Input type="number" value={siteForm.publication_price} onChange={(e) => setSiteForm({...siteForm, publication_price: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px]">{t("pub.writePrice")} (PLN)</Label>
                      <Input type="number" value={siteForm.writing_price} onChange={(e) => setSiteForm({...siteForm, writing_price: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px]">{t("pub.linkPrice")} (PLN)</Label>
                      <Input type="number" value={siteForm.link_price} onChange={(e) => setSiteForm({...siteForm, link_price: parseFloat(e.target.value) || 0})} />
                    </div>
                  </div>

                  {/* Reczna akceptacja */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div>
                      <p className="text-xs font-medium text-foreground">{t("pub.manualApproval")}</p>
                      <p className="text-[9px] text-muted-foreground">{t("pub.manualApprovalHint")}</p>
                    </div>
                    <button type="button" onClick={() => setSiteForm({...siteForm, manual_approval: !siteForm.manual_approval})}
                      className={`w-9 h-5 rounded-full transition-colors shrink-0 ${siteForm.manual_approval ? "bg-primary" : "bg-slate-300 dark:bg-slate-700"}`}>
                      <div className={`w-4 h-4 rounded-full bg-white transition-transform ${siteForm.manual_approval ? "translate-x-4" : "translate-x-0.5"}`} />
                    </button>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("pub.description")}</Label>
                    <Textarea value={siteForm.description} onChange={(e) => setSiteForm({...siteForm, description: e.target.value})} rows={2} className="resize-none" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddDialog(false)}>{t("sites.cancel")}</Button>
                  <Button data-testid="save-pub-site" onClick={handleAddSite}>{t("sites.save")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {sites.length === 0 ? (
            <Card className="rounded-xl border shadow-sm bg-card">
              <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Globe className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                <p className="text-xs">{t("pub.noSites")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Lista stron */}
              <div className={selectedSite ? "" : "lg:col-span-3"}>
                <div className="grid grid-cols-1 gap-2">
                  {filteredSites.map((site) => (
                    <button key={site.id} data-testid={`pub-site-${site.id}`}
                      onClick={() => openSiteDetail(site)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedSite?.id === site.id ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/30 bg-card"
                      }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-semibold text-foreground">{site.name}</span>
                            <Badge variant="secondary" className="text-[8px] rounded-md">{site.site_type === "wordpress" ? "WP" : site.site_type === "html" ? "HTML" : "Inne"}</Badge>
                            {site.wp_api_user && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[8px] border-0 rounded-md">API</Badge>}
                            {site.manual_approval && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[8px] border-0 rounded-md">Reczna</Badge>}
                            {site.ai_analysis && <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 text-[8px] border-0 rounded-md">AI</Badge>}
                          </div>
                          <p className="text-[10px] text-muted-foreground truncate">{site.url}</p>
                        </div>
                        {site.pending_orders > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 text-[9px] border-0 rounded-md shrink-0">{site.pending_orders}</Badge>
                        )}
                      </div>
                      <div className="flex gap-1 mt-1.5">
                        {site.category && <Badge variant="outline" className="text-[8px] rounded-md">{site.category}</Badge>}
                        {site.offer && <span className="text-[9px] text-muted-foreground">od {site.offer.link_price} PLN</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Detail panel */}
              {selectedSite && (
                <div className="lg:col-span-2">
                  <Card className="rounded-xl border shadow-sm bg-card">
                    <CardHeader className="pb-2 pt-4 px-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base font-heading text-foreground">{selectedSite.name}</CardTitle>
                          <a href={selectedSite.url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5">
                            {selectedSite.url} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        </div>
                        <div className="flex gap-1.5">
                          <Badge variant="secondary" className="text-[9px]">{selectedSite.site_type === "wordpress" ? "WordPress" : selectedSite.site_type}</Badge>
                          {selectedSite.wp_api_user && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] border-0 rounded-md">API Connected</Badge>}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="px-5 pb-5 pt-2">
                      <Tabs value={siteTab} onValueChange={setSiteTab}>
                        <TabsList className="mb-3">
                          <TabsTrigger value="overview" className="text-xs">Przeglad</TabsTrigger>
                          <TabsTrigger value="analysis" className="text-xs">Analiza AI</TabsTrigger>
                          <TabsTrigger value="pricing" className="text-xs">Cennik</TabsTrigger>
                          <TabsTrigger value="widget" className="text-xs">Widget</TabsTrigger>
                        </TabsList>

                        {/* PRZEGLAD */}
                        <TabsContent value="overview" className="space-y-3">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <div className="p-2 rounded-lg bg-muted/50">
                              <Tag className="w-3.5 h-3.5 text-muted-foreground mb-1" strokeWidth={1.5} />
                              <p className="text-xs font-semibold text-foreground">{selectedSite.category || "—"}</p>
                              <p className="text-[9px] text-muted-foreground">Kategoria</p>
                            </div>
                            <div className="p-2 rounded-lg bg-muted/50">
                              <MapPin className="w-3.5 h-3.5 text-muted-foreground mb-1" strokeWidth={1.5} />
                              <p className="text-xs font-semibold text-foreground">{selectedSite.region || "—"}</p>
                              <p className="text-[9px] text-muted-foreground">Region</p>
                            </div>
                            <div className="p-2 rounded-lg bg-muted/50">
                              <BarChart3 className="w-3.5 h-3.5 text-muted-foreground mb-1" strokeWidth={1.5} />
                              <p className="text-xs font-semibold text-foreground">{selectedSite.monthly_visitors ? `~${selectedSite.monthly_visitors}/m` : "—"}</p>
                              <p className="text-[9px] text-muted-foreground">Ruch</p>
                            </div>
                            <div className="p-2 rounded-lg bg-muted/50">
                              <ShoppingBag className="w-3.5 h-3.5 text-muted-foreground mb-1" strokeWidth={1.5} />
                              <p className="text-xs font-semibold text-foreground">{selectedSite.orders_count || 0}</p>
                              <p className="text-[9px] text-muted-foreground">Zamowien</p>
                            </div>
                          </div>

                          {/* Oferta cenowa */}
                          {selectedSite.offer && (
                            <div className="grid grid-cols-3 gap-2 p-2 rounded-lg bg-muted/50">
                              <div className="text-center">
                                <p className="text-xs font-bold text-blue-600">{selectedSite.offer.publication_price} PLN</p>
                                <p className="text-[8px] text-muted-foreground">Publikacja</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-bold text-violet-600">{selectedSite.offer.writing_price} PLN</p>
                                <p className="text-[8px] text-muted-foreground">Pisanie</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-bold text-emerald-600">{selectedSite.offer.link_price} PLN</p>
                                <p className="text-[8px] text-muted-foreground">Link</p>
                              </div>
                            </div>
                          )}

                          {/* Akcje */}
                          <div className="flex flex-wrap gap-2">
                            {selectedSite.site_type === "wordpress" && (
                              <>
                                <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
                                  onClick={() => handleTestConnection(selectedSite)} disabled={testingWp}>
                                  {testingWp ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wifi className="w-3 h-3" strokeWidth={1.5} />}
                                  Test polaczenia
                                </Button>
                                <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
                                  onClick={() => handleTestPublish(selectedSite)}>
                                  <FileText className="w-3 h-3" strokeWidth={1.5} />
                                  Test publikacji WP
                                </Button>
                              </>
                            )}
                          </div>
                          {selectedSite.last_wp_test && (
                            <p className="text-[9px] text-muted-foreground">
                              Ostatni test: {new Date(selectedSite.last_wp_test).toLocaleString()} {selectedSite.wp_test_result?.success ? "- OK" : "- BLAD"}
                            </p>
                          )}
                        </TabsContent>

                        {/* ANALIZA AI */}
                        <TabsContent value="analysis" className="space-y-3">
                          <Button size="sm" onClick={() => handleAnalyzeSite(selectedSite)} disabled={analyzing} className="gap-1.5 text-xs">
                            {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            {analyzing ? "Analizowanie..." : "Uruchom analize AI"}
                          </Button>

                          {selectedSite.ai_analysis && (
                            <ScrollArea className="max-h-[350px]">
                              <div className="space-y-2">
                                {selectedSite.ai_analysis.categories?.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {selectedSite.ai_analysis.categories.map((c, i) => <Badge key={i} variant="secondary" className="text-[9px]">{c}</Badge>)}
                                  </div>
                                )}
                                {selectedSite.ai_analysis.target_audience && (
                                  <p className="text-xs text-muted-foreground"><Users className="w-3 h-3 inline mr-1" />{selectedSite.ai_analysis.target_audience}</p>
                                )}
                                {selectedSite.ai_analysis.top_search_phrases?.length > 0 && (
                                  <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-[9px] uppercase font-semibold text-muted-foreground mb-1">TOP frazy Google</p>
                                    {selectedSite.ai_analysis.top_search_phrases.map((p2, i) => (
                                      <Badge key={i} variant="outline" className="text-[9px] rounded-md mr-1 mb-1">{p2}</Badge>
                                    ))}
                                  </div>
                                )}
                                {selectedSite.ai_analysis.suggested_topics?.length > 0 && (
                                  <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-[9px] uppercase font-semibold text-muted-foreground mb-1">Propozycje tematow</p>
                                    {selectedSite.ai_analysis.suggested_topics.map((t2, i) => (
                                      <p key={i} className="text-[10px] text-foreground py-0.5 pl-2 border-l-2 border-primary/40">{i+1}. {t2}</p>
                                    ))}
                                  </div>
                                )}
                                {selectedSite.ai_analysis.strengths?.length > 0 && (
                                  <div className="p-2 rounded-lg bg-muted/50">
                                    <p className="text-[9px] uppercase font-semibold text-muted-foreground mb-1">Mocne strony</p>
                                    {selectedSite.ai_analysis.strengths.map((s, i) => (
                                      <p key={i} className="text-[10px] text-foreground py-0.5 pl-2 border-l-2 border-emerald-400">{s}</p>
                                    ))}
                                  </div>
                                )}
                                {selectedSite.ai_analysis.keywords?.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {selectedSite.ai_analysis.keywords.map((kw, i) => <Badge key={i} variant="outline" className="text-[8px]">{kw}</Badge>)}
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          )}
                        </TabsContent>

                        {/* CENNIK (edytowalny) */}
                        <TabsContent value="pricing" className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => handleGeneratePricing(selectedSite)} disabled={pricingLoading}>
                              {pricingLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                              Generuj cennik AI
                            </Button>
                            {pricingItems.length > 0 && !editingPricing && (
                              <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={async () => {
                                setEditingPricing(true);
                                try { const res = await axios.get(`${API}/sites/wp/${selectedSite.id}/wp-categories`); setWpCategories(res.data || []); } catch {}
                              }}>
                                <Pencil className="w-3 h-3" /> Edytuj
                              </Button>
                            )}
                            {editingPricing && (
                              <>
                                <Button size="sm" className="gap-1 text-xs" disabled={savingPricing}
                                  onClick={async () => {
                                    await handleSavePricing(selectedSite, { ...selectedSite.pricing, pricing: pricingItems });
                                    setEditingPricing(false);
                                  }}>
                                  {savingPricing ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
                                  Zapisz
                                </Button>
                                <Button variant="ghost" size="sm" className="text-xs" onClick={() => setEditingPricing(false)}>Anuluj</Button>
                              </>
                            )}
                          </div>
                          
                          {/* Info o prowizji portalu */}
                          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                            <div className="flex items-start gap-2">
                              <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                              <div className="text-xs text-blue-800 dark:text-blue-200">
                                <p className="font-semibold flex items-center gap-1">
                                  <Percent className="w-3 h-3" /> Prowizja portalu: {commissionPercent}%
                                </p>
                                <p className="text-blue-600 dark:text-blue-300 mt-0.5">
                                  Portal pobiera {commissionPercent}% prowizji od reklamodawcy. Ty otrzymujesz pelna kwote cennika.
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {pricingItems.length === 0 ? (
                            <p className="text-xs text-muted-foreground text-center py-6">Brak cennika. Wygeneruj cennik AI.</p>
                          ) : (
                            <ScrollArea className="max-h-[300px]">
                              <div className="space-y-1">
                                {pricingItems.map((item, i) => (
                                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${item.enabled !== false ? "bg-card" : "bg-muted/30 opacity-60"}`}>
                                    <span className="flex-1 text-[10px] font-medium text-foreground truncate">{item.name}</span>
                                    {editingPricing ? (
                                      <div className="space-y-1">
                                        <div className="flex items-center gap-1">
                                          <Input type="number" className="w-16 h-7 text-right text-xs" value={item.price_pln}
                                            onChange={(e) => setPricingItems(prev => prev.map((it, idx) => idx === i ? {...it, price_pln: parseFloat(e.target.value) || 0} : it))} />
                                          <span className="text-[9px] text-muted-foreground">PLN</span>
                                          <button onClick={() => setPricingItems(prev => prev.map((it, idx) => idx === i ? {...it, enabled: !it.enabled} : it))}
                                            className={`w-7 h-4 rounded-full ${item.enabled !== false ? "bg-emerald-500" : "bg-slate-300"}`}>
                                            <div className={`w-3 h-3 rounded-full bg-white transition-transform ${item.enabled !== false ? "translate-x-3" : "translate-x-0.5"}`} />
                                          </button>
                                        </div>
                                        <select value={item.wp_category_id || ""} onChange={(e) => {
                                          const cat = wpCategories.find(c => String(c.id) === e.target.value);
                                          setPricingItems(prev => prev.map((it, idx) => idx === i ? {...it, wp_category_id: e.target.value, wp_category_name: cat?.name || ""} : it));
                                        }} className="w-full h-6 text-[9px] rounded border bg-white px-1">
                                          <option value="">-- Kategoria WP --</option>
                                          {wpCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                      </div>
                                    ) : (
                                      <div className="text-right">
                                        <span className="text-xs font-bold text-primary">{item.price_pln} PLN</span>
                                        {item.wp_category_name && <p className="text-[8px] text-blue-500">{item.wp_category_name}</p>}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </ScrollArea>
                          )}
                        </TabsContent>

                        {/* WIDGET EMBED */}
                        <TabsContent value="widget" className="space-y-4">
                          <div className="space-y-3">
                            <div className="p-4 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                              <h4 className="font-bold text-sm mb-1">Widget "Zamow reklame"</h4>
                              <p className="text-blue-100 text-xs">
                                Skopiuj kod i wklej na swoja strone. Widget pokaże oferte i przekieruje klientow do Epidemos.
                              </p>
                            </div>
                            
                            {/* Pelny widget */}
                            <div className="space-y-2">
                              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5" /> Pelny widget (z animacja)
                              </label>
                              <div className="relative">
                                <pre className="p-3 rounded-lg bg-slate-900 text-green-400 text-[10px] overflow-x-auto whitespace-pre-wrap font-mono">
{`<!-- Epidemos Widget -->
<div id="epidemos-widget-${selectedSite?.id?.substring(0, 8) || 'xxx'}"></div>
<script>
(function() {
  var s = document.createElement("script");
  s.src = "https://epidemos.pl/api/embed/${selectedSite?.id || 'SITE_ID'}/script.js";
  s.async = true;
  document.getElementById("epidemos-widget-${selectedSite?.id?.substring(0, 8) || 'xxx'}").appendChild(s);
})();
</script>`}
                                </pre>
                                <Button 
                                  size="sm" 
                                  variant="secondary"
                                  className="absolute top-2 right-2 h-6 text-[10px] gap-1"
                                  onClick={() => {
                                    const code = `<!-- Epidemos Widget -->\n<div id="epidemos-widget-${selectedSite?.id?.substring(0, 8)}"></div>\n<script>\n(function() {\n  var s = document.createElement("script");\n  s.src = "https://epidemos.pl/api/embed/${selectedSite?.id}/script.js";\n  s.async = true;\n  document.getElementById("epidemos-widget-${selectedSite?.id?.substring(0, 8)}").appendChild(s);\n})();\n</script>`;
                                    navigator.clipboard.writeText(code);
                                    toast.success("Kod skopiowany!");
                                  }}
                                >
                                  Kopiuj
                                </Button>
                              </div>
                            </div>
                            
                            {/* Prosty link */}
                            <div className="space-y-2">
                              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                <Link2 className="w-3.5 h-3.5" /> Prosty przycisk (bez JS)
                              </label>
                              <div className="relative">
                                <pre className="p-3 rounded-lg bg-slate-900 text-green-400 text-[10px] overflow-x-auto whitespace-pre-wrap font-mono">
{`<a href="https://epidemos.pl/zamow/${selectedSite?.id || 'SITE_ID'}" 
   target="_blank"
   style="display:inline-block;padding:12px 24px;
          background:#2563eb;color:white;
          text-decoration:none;border-radius:8px;
          font-family:sans-serif;font-weight:bold;">
  Zamow reklame na tej stronie
</a>`}
                                </pre>
                                <Button 
                                  size="sm" 
                                  variant="secondary"
                                  className="absolute top-2 right-2 h-6 text-[10px] gap-1"
                                  onClick={() => {
                                    const code = `<a href="https://epidemos.pl/zamow/${selectedSite?.id}" target="_blank" style="display:inline-block;padding:12px 24px;background:#2563eb;color:white;text-decoration:none;border-radius:8px;font-family:sans-serif;font-weight:bold;">Zamow reklame na tej stronie</a>`;
                                    navigator.clipboard.writeText(code);
                                    toast.success("Kod skopiowany!");
                                  }}
                                >
                                  Kopiuj
                                </Button>
                              </div>
                            </div>
                            
                            {/* Link do zamowienia */}
                            <div className="space-y-2">
                              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                <ExternalLink className="w-3.5 h-3.5" /> Bezposredni link
                              </label>
                              <div className="flex items-center gap-2">
                                <Input 
                                  readOnly 
                                  value={`https://epidemos.pl/zamow/${selectedSite?.id || 'SITE_ID'}`}
                                  className="h-8 text-xs font-mono bg-muted"
                                />
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-8 text-xs shrink-0"
                                  onClick={() => {
                                    navigator.clipboard.writeText(`https://epidemos.pl/zamow/${selectedSite?.id}`);
                                    toast.success("Link skopiowany!");
                                  }}
                                >
                                  Kopiuj
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-8 text-xs shrink-0"
                                  onClick={() => window.open(`https://epidemos.pl/zamow/${selectedSite?.id}`, '_blank')}
                                >
                                  Otworz
                                </Button>
                              </div>
                            </div>
                            
                            {/* Podglad */}
                            <div className="space-y-2 pt-2">
                              <label className="text-xs font-semibold text-foreground">Podglad widgetu</label>
                              <div className="p-4 rounded-lg bg-slate-100 dark:bg-slate-800 flex justify-center">
                                <div style={{
                                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                                  borderRadius: "12px",
                                  padding: "20px",
                                  maxWidth: "300px",
                                  boxShadow: "0 4px 20px rgba(37, 99, 235, 0.3)",
                                  color: "white"
                                }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                                    <div style={{ width: "28px", height: "28px", background: "white", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", color: "#2563eb", fontSize: "12px" }}>E</div>
                                    <div style={{ fontSize: "12px", fontWeight: "600", opacity: "0.9" }}>Epidemos</div>
                                  </div>
                                  <div style={{ fontSize: "16px", fontWeight: "700", marginBottom: "8px" }}>Zamow reklame na tej stronie</div>
                                  <div style={{ fontSize: "24px", fontWeight: "800", marginBottom: "14px" }}>
                                    od {selectedSite?.offer?.publication_price || selectedSite?.pricing?.pricing?.[0]?.price || 25} <span style={{ fontSize: "14px", fontWeight: "500", opacity: "0.8" }}>PLN</span>
                                  </div>
                                  <div style={{ display: "block", width: "100%", padding: "12px", background: "white", color: "#2563eb", borderRadius: "8px", fontSize: "14px", fontWeight: "700", textAlign: "center" }}>
                                    Zamow teraz
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ORDERS TAB */}
        <TabsContent value="orders" className="mt-4 space-y-4">
          <div className="flex items-center gap-2">
            <Select value={orderFilter} onValueChange={setOrderFilter}>
              <SelectTrigger className="h-8 text-xs w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">{t("queue.all")}</SelectItem>
                <SelectItem value="pending" className="text-xs">{t("pub.pending")}</SelectItem>
                <SelectItem value="accepted" className="text-xs">{t("pub.accepted")}</SelectItem>
                <SelectItem value="completed" className="text-xs">{t("pub.completed")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-0">
              {orders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <ShoppingBag className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{t("pub.noOrders")}</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[500px]">
                  <div className="divide-y divide-border">
                    {orders.map((order) => (
                      <div key={order.id} data-testid={`pub-order-${order.id}`} className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">{order.title || order.order_type}</p>
                            <p className="text-[10px] text-muted-foreground">{order.site_name} &middot; {order.order_type} &middot; {new Date(order.created_at).toLocaleDateString()}</p>
                            {order.content && <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{order.content.substring(0, 100)}</p>}
                            {!order.content && !order.generated_content && (
                              <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Wydawca pisze tresc
                              </p>
                            )}
                            {order.generated_content && (
                              <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Artykul wygenerowany
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1.5">
                              <Badge className={`text-[9px] border-0 rounded-md ${statusBadge[order.status] || statusBadge.pending}`}>{order.status}</Badge>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-foreground">{order.price} PLN</p>
                            <div className="flex flex-wrap gap-1 mt-2 justify-end">
                              {/* Przycisk generowania artykulu - dla zamowien bez tresci */}
                              {!order.content && !order.generated_content && order.status !== "rejected" && (
                                <Button size="sm" className="h-7 text-[10px] gap-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white"
                                  onClick={() => handleGenerateArticle(order.id)} disabled={generating === order.id}>
                                  {generating === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                  Wygeneruj artykul
                                </Button>
                              )}
                              {order.status === "pending" && (
                                <>
                                  <Button size="sm" className="h-7 text-[10px] gap-1 rounded-md text-emerald-600" variant="outline"
                                    onClick={() => handleOrderAction(order.id, "accept")} disabled={processing === order.id}>
                                    <CheckCircle2 className="w-3 h-3" /> {t("pub.accept")}
                                  </Button>
                                  <Button size="sm" className="h-7 text-[10px] gap-1 rounded-md text-red-500" variant="outline"
                                    onClick={() => handleOrderAction(order.id, "reject")} disabled={processing === order.id}>
                                    <XCircle className="w-3 h-3" /> {t("pub.reject")}
                                  </Button>
                                </>
                              )}
                              {order.status === "accepted" && (
                                <Button size="sm" className="h-7 text-[10px] gap-1 rounded-md" variant="outline"
                                  onClick={() => handleOrderAction(order.id, "complete")} disabled={processing === order.id}>
                                  <Send className="w-3 h-3" /> {t("pub.markComplete")}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
